import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { User } from '@features/Login/types';

export interface UserState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  expireTimer: string | number;
  resetToken: string | null;
  loginTime: string | null;
  lastActivity: string | null;
  sessionId: string | null;
}

const initialState: UserState = {
  user: null,
  token: null,
  isAuthenticated: false,
  expireTimer: '',
  resetToken: null,
  loginTime: null,
  lastActivity: null,
  sessionId: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.loginTime = new Date().toISOString();
      state.lastActivity = new Date().toISOString();
      state.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    },
    clearUser: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loginTime = null;
      state.lastActivity = null;
      state.sessionId = null;
      state.expireTimer = '';
      state.resetToken = null;
    },
    setExpireTimer: (state, action: PayloadAction<string>) => {
      state.expireTimer = action.payload;
    },
    setResetToken: (state, action: PayloadAction<string>) => {
      state.resetToken = action.payload;
    },
    initializeUser: (state, action: PayloadAction<{ user: User | null; token: string | null }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = !!action.payload.token;
    },
    updateLastActivity: (state) => {
      state.lastActivity = new Date().toISOString();
    },
    updateSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    updateUserProfile: (
      state,
      action: PayloadAction<
        Partial<Pick<User, 'firstName' | 'lastName' | 'phoneNumber' | 'emailAddress'>>
      >,
    ) => {
      if (state.user) {
        Object.assign(state.user, action.payload);
      }
    },
  },
});

export const {
  setUser,
  clearUser,
  setExpireTimer,
  setResetToken,
  initializeUser,
  updateLastActivity,
  updateSessionId,
  updateUserProfile,
} = userSlice.actions;
export default userSlice.reducer; 